import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCallback } from 'react'

/**
 * Hook for navigation within courses that preserves the profileId.
 * This is necessary to disambiguate cohorts with the same slug but different profileId.
 */
export const useCourseNavigation = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const profileId = searchParams.get('profileId')

    /**
     * Builds a URL for course navigation, preserving the profileId if it exists.
     */
    const buildCourseUrl = useCallback(
        (slugOrId: string, stepId?: string, extraParams?: Record<string, string>) => {
            const basePath = stepId ? `/${slugOrId}/${stepId}` : `/${slugOrId}`
            const params = new URLSearchParams()

            // Preserve profileId if it exists
            if (profileId) {
                params.set('profileId', profileId)
            }

            // Add extra params
            if (extraParams) {
                Object.entries(extraParams).forEach(([key, value]) => {
                    params.set(key, value)
                })
            }

            const queryString = params.toString()
            return queryString ? `${basePath}?${queryString}` : basePath
        },
        [profileId]
    )

    /**
     * Navigates to a course route, preserving the profileId.
     */
    const navigateToCourse = useCallback(
        (slugOrId: string, stepId?: string, extraParams?: Record<string, string>) => {
            navigate(buildCourseUrl(slugOrId, stepId, extraParams))
        },
        [navigate, buildCourseUrl]
    )

    return {
        profileId,
        buildCourseUrl,
        navigateToCourse,
    }
}
